/**
 * The tokens this surface needs and `pokpokTheme` does not carry.
 *
 * # Why this file exists at all, and why it is not `pokpokTheme.ts`
 *
 * Every colour on the guest surface should come from `@pokpok/astryx`, because a
 * cross-repo test there holds those values to `pokpok_tokens/palette.dart` and
 * that is the only thing stopping the till and the web page drifting into two
 * different reds. Nine tenths of the QR design maps onto that theme cleanly.
 *
 * The remainder does not, and the honest options were: (a) inline the design's
 * hexes in whichever screen needed them, (b) push new tokens into astryx, or
 * (c) declare them once here.
 *
 * (a) is how a design system dies — thirteen screens, eight authors, one
 * afternoon, and the warm hairline becomes four slightly different greys.
 * (b) is the right long-term answer for at least the hairline, but astryx is
 * shared with the Flutter till and the KDS: adding a token there means choosing
 * its dark-surface value too, and nobody has drawn a dark guest QR page or a
 * warm hairline on a kitchen display. Inventing the dark half to satisfy a light
 * screen is exactly the pair-collapsing guess `defineTheme.ts` warns about.
 *
 * So (c): one file, one emission point, every value justified below, and a
 * standing escalation that the first four belong in astryx the day somebody
 * decides what they are on a dark surface.
 *
 * # These are light-surface values only
 *
 * D-owner decision, 2026-08-13: the guest QR surface ships light only. There is
 * no `[light, dark]` pair here because there is no dark surface to pair with —
 * see `ThemeStyles.tsx` for the whole argument. If this surface ever gains a
 * dark scheme, this file is where the second half of each value has to be
 * chosen, and every value below needs a designer rather than a developer.
 *
 * # Why literals rather than `color-mix()` off an existing token
 *
 * Deriving `#ECE7E0` as, say, `color-mix(in srgb, var(--color-background-body)
 * 60%, var(--color-text-primary) 6%)` would be self-documenting and would track
 * a Sticky Rice change automatically. It is not used because `color-mix` needs
 * Chrome 111 / Safari 16.2, and this is the one surface whose hardware the
 * merchant does not own: a guest's four-year-old Android on dining-room 4G. A
 * token that resolves to nothing paints a transparent border, and a borderless
 * card on a stickyRice ground is an invisible card. The design package's own
 * hexes are used instead, and each is a member of the Sticky Rice family the
 * designer drew, not a value picked here.
 */

/** Every custom property this file owns, so the emission and the docs cannot drift. */
export const SURFACE_TOKENS: Readonly<Record<string, string>> = {
  /*
   * THE WARM HAIRLINE — the most-used colour in the design package (23 uses):
   * every divider, card border, input border, menu-row rule and totals rule.
   *
   * `--color-border` is `#E4E7EC`, a cool grey built for a white till. Against
   * `#F7F3EE` it reads faintly blue, and since it is the line that separates
   * literally everything on this surface, that one wrong hue is the whole page.
   * `#ECE7E0` is Sticky Rice's own family, one step down.
   */
  '--pokpok-color-border-warm': '#ECE7E0',

  /*
   * THE DISABLED PRIMARY FILL — item detail, while a required option is
   * unanswered.
   *
   * Not `--color-accent` at reduced opacity: the design draws a desaturated red
   * (`#D6A9A9`) so the button keeps white text at full strength and the label
   * stays readable. Opacity would fade the label with the fill and produce a
   * button a guest cannot read *and* cannot press.
   *
   * There is no disabled-accent token in astryx because the till has no disabled
   * primary — a POS button that cannot be pressed is a button that is not drawn.
   */
  '--pokpok-color-accent-disabled': '#D6A9A9',

  /*
   * THE ACCENT-MUTED PAIR — border and ink for a notice drawn on
   * `--color-accent-muted` (`#FDE8E6`), used once, for the cart's "you will pay
   * at the counter" note.
   *
   * astryx carries the fill and neither of the other two. `--color-error` on
   * `#FDE8E6` is a warning the notice is not making — nothing has gone wrong,
   * the guest is simply being told when money changes hands — so the design's
   * darker, browner red is the correct reading and `#B42318` is not.
   */
  '--pokpok-color-accent-muted-border': '#F5C9C4',
  '--pokpok-color-accent-muted-ink': '#8B3B33',

  /*
   * THE MONEY-VERIFIED BORDER. astryx has the Pandan text colour and the
   * `#E8F5EE` surface but no border between them, and a card drawn in
   * `--pokpok-color-money-verified-surface` on a Sticky Rice ground needs an
   * edge or it dissolves. `--pokpok-color-border-warm` would work and would be
   * wrong: this is the one card on the surface that is allowed to be green, and
   * a warm-grey edge around it reads as a mistake.
   *
   * MONEY RULE: this token is Pandan-family and is therefore governed by
   * `src/order/moneyTone.ts` like every other green. It may appear only on a
   * card whose subject is server-verified money. `Card tone="verified"` is the
   * only sanctioned consumer.
   */
  '--pokpok-color-money-verified-border': '#BFE3CC',

  /*
   * THE PHOTO PLACEHOLDER. A gradient rather than two colour tokens, so no
   * screen can take the stripe colours apart and use one of them for something
   * else. It paints *behind* a real `<img>` — while it decodes, and in the gap
   * where a photo is slow — and it is never a standalone box: `test/
   * guest-path.test.tsx` asserts that an item with no `image_url` renders no
   * image element at all, because the no-photo layout IS the layout on this
   * product (most V1 merchants have no photography). See `Photo` in `src/ui`.
   *
   * `--color-skeleton` is the nearest existing role and is a cool grey; loading
   * text bars keep using it, because `index.html`'s pre-bundle skeleton is
   * painted in it and a second skeleton look on the same page is worse than a
   * slightly cool one.
   */
  '--pokpok-photo-placeholder':
    'repeating-linear-gradient(135deg, #EFE7DA 0 8px, #E7DECF 8px 16px)',

  /*
   * THE MISSING TYPE STEP, and the single most consequential line in this file.
   *
   * `pokpokTheme` emits three scale steps and `global.css` collapses two of them
   * to hold the 16px Thai floor, so the surface ships **16px and 27.648px**.
   * The design package uses nineteen sizes, and eight of them — every screen
   * title, the sheet title, the payment amount, the success headline — sit in a
   * band between those two numbers that does not exist. Rendering all eight at
   * 27.648px makes five consecutive screens open with a headline the width of
   * the viewport; rendering them at 16px deletes the hierarchy entirely.
   *
   * So one step, and exactly one, at **+2 on the theme's own modular scale**:
   * 16 × 1.2² = 23.04px. It is not an invented size — it is the step astryx
   * already implies and simply does not emit, and it lands on the design's most
   * common title size (23px) to two decimal places.
   *
   * Leading is 1.35, between `global.css`'s 1.3 for h1–h3 and the 1.6 body
   * floor. The floor is a body-copy rule; a 23px line does not clip Thai tone
   * marks at 1.35, and 1.6 on a title reads as a paragraph.
   *
   * THIS IS THE ONLY INTERMEDIATE SIZE ON THE SURFACE. A screen that wants 19px
   * or 21px uses this or uses body — the two-token-sizes problem is the exact
   * failure `global.css`'s supporting-token block was written to end, and
   * re-opening it per screen would undo that at thirteen times the cost.
   */
  '--pokpok-text-title-size': '23.04px',
  '--pokpok-text-title-leading': '31.104px',

  /*
   * THE CONTROL BORDER WIDTH. `--pokpok-border-width` is 1px and its own comment
   * in `pokpokTheme.ts` says it "is not a ratified design step" and asks whether
   * the system ever needs a second width. The QR package answers: yes, and it
   * uses the two consistently — 1px for anything structural (dividers, card and
   * input edges) and 1.5px for anything that is a control or carries state
   * (chips, outline buttons, the destructive button). At 1px an outline button
   * next to a card edge reads as another card.
   *
   * ESCALATION for 00-foundations: this is a second border width in production
   * on a guest surface, decided here because thirteen screens needed it this
   * week. If foundations ratifies a different number, this is the one line to
   * change.
   */
  '--pokpok-border-width-control': '1.5px',

  /*
   * THE GUTTER. Not a new value — an alias for `--pokpok-space-4` (16px), which
   * is what `pokpokGrid.compact.margin` already is. It exists as a name because
   * the design draws a 20px gutter on every screen without exception and astryx
   * has no 20px step, so thirteen screens are each about to make the same
   * decision. One name means the day somebody measures a real phone and wants
   * 20px back, it is one line rather than thirteen files.
   */
  '--pokpok-gutter': 'var(--pokpok-space-4)',
};

/** The tokens above as a CSS rule body, for the `<style>` in `ThemeStyles`. */
export function surfaceTokensCss(): string {
  return Object.entries(SURFACE_TOKENS)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');
}
